import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { DialogModule } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ButtonModule } from 'primeng/button';
import { ImageModule } from 'primeng/image';
import { finalize } from 'rxjs';
import { SchedaInterventoService } from '@/core/service/scheda-intervento.service';
import { MessageService } from 'primeng/api';
import { FormArray, FormGroup } from '@angular/forms';

@Component({
    selector: 'app-indagine-immagini',
    standalone: true,
    imports: [CommonModule, DialogModule, ProgressSpinnerModule, ButtonModule, ImageModule],
    templateUrl: './indagine-immagini.component.html',
    providers: [MessageService]
})
export class IndagineImmaginiComponent implements OnChanges {
    @Input() deviatoioId!: number;
    @Input() dataRilevamento!: Date;
    @Input() form!: FormGroup;

    // Stato locale
    selectedFiles: any[] = [];
    fileDaCaricare: any[] = [];
    isImageLoading = false;
    isTabLoading = false;

    // Dialog preview
    imageDialogVisible = false;
    currentImageUrl: string | null = null;
    currentImage: string | null = null;

    // Dialog conferma upload
    confirmDialogVisible = false;

    isDragging = false;

    constructor(
        private schedaService: SchedaInterventoService,
        private messageService: MessageService
    ) {}

    ngOnChanges(changes: SimpleChanges) {
        if (this.deviatoioId && this.dataRilevamento && (changes['deviatoioId'] || changes['dataRilevamento'])) {
            this.caricaListaFile();
        }
    }

    caricaListaFile() {
        if (!this.deviatoioId || !this.dataRilevamento || !this.interventi) return;

        this.isTabLoading = true;
        for (const intervento of this.interventi.controls) {
            const uploadFoto = {
                deviatoioId: this.deviatoioId,
                interventoId: intervento?.value?.id,
                dateFolder: this.dataRilevamento.toISOString().split('T')[0],
                fileName: null
            };

            this.schedaService
                .getPreviewList(uploadFoto)
                .pipe(finalize(() => (this.isTabLoading = false)))
                .subscribe({
                    next: (files: string[]) => {
                        this.selectedFiles = files.map((name) => ({ name }));
                    },
                    error: (err) => console.error('Errore caricamento lista:', err)
                });
        }
    }

    openDialog(file: any) {
        this.isImageLoading = true;
        this.imageDialogVisible = true;
        this.currentImage = file.name;

        const dto = {
            deviatoioId: this.deviatoioId,
            fileName: file.name,
            dateFolder: this.dataRilevamento.toISOString().split('T')[0]
        };

        this.schedaService.getFilePreview(dto).subscribe({
            next: (blob) => {
                this.currentImageUrl = URL.createObjectURL(blob);
                this.isImageLoading = false;
            },
            error: (err) => {
                console.error('Errore preview:', err);
                this.isImageLoading = false;
            }
        });
    }

    onFileSelected(event: any): void {
        const files = Array.from(event.target.files) as File[];
        this.handleSelectedFiles(files);
        event.target.value = '';
    }

    handleSelectedFiles(files: File[]) {
        const maxSize = 5 * 1024 * 1024;

        files.forEach((file) => {
            if (file.size > maxSize || !file.type.startsWith('image/')) {
                this.messageService.add({
                    severity: 'warn',
                    summary: 'File non valido',
                    detail: `${file.name} è troppo grande o non è un'immagine`
                });
                return;
            }

            const reader = new FileReader();
            reader.onload = () => {
                this.fileDaCaricare.push({
                    file,
                    preview: reader.result as string,
                    name: file.name,
                    size: file.size,
                    selected: false
                });
            };
            reader.readAsDataURL(file);
        });

        if (files.length > 0) {
            this.confirmDialogVisible = true;
        }
    }

    uploadOD() {
        if (this.fileDaCaricare.length === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Nessun file',
                detail: 'Seleziona almeno un file da caricare'
            });
            return;
        }

        if (!this.deviatoioId) {
            this.messageService.add({
                severity: 'error',
                summary: 'Errore',
                detail: 'deviatoioId mancante'
            });
            return;
        }

        // Usa la data corrente se non è stata fornita
        const dataRilevamento = this.dataRilevamento || new Date();

        this.isImageLoading = true;
        const formData = new FormData();

        // Aggiungi tutti i file
        for (const item of this.fileDaCaricare) {
            const file: File = item.file;
            formData.append('files', file, file.name);
        }

        // Aggiungi UN SOLO DTO con i dati comuni
        const dto = {
            deviatoioId: this.deviatoioId,
            dateFolder: dataRilevamento.toISOString().split('T')[0],
            fileName: null, // Il backend prenderà i nomi dai file
            interventoId: null
        };

        // Il backend riceve 'data' come @RequestPart String e la parsa a mano: stringa semplice.
        formData.append('data', JSON.stringify(dto));

        this.schedaService
            .uploadOD(formData)
            .pipe(
                finalize(() => {
                    this.isImageLoading = false;
                })
            )
            .subscribe({
                next: () => {
                    this.messageService.add({ severity: 'success', summary: 'Upload completato' });
                    this.confirmDialogVisible = false;
                    this.fileDaCaricare = [];
                    this.caricaListaFile();
                },
                error: () => {
                    this.messageService.add({ severity: 'error', summary: 'Errore durante upload' });
                }
            });
    }

    onDialogHide() {
        this.imageDialogVisible = false;
        this.currentImage = null;
        this.currentImageUrl = null;
    }

    hasSelectedFiles(): boolean {
        return this.fileDaCaricare.some((f) => f.selected);
    }

    toggleFileSelection(file: any) {
        file.selected = !file.selected;
    }

    removeSelectedFiles() {
        this.fileDaCaricare = this.fileDaCaricare.filter((f) => !f.selected);
        if (this.fileDaCaricare.length === 0) {
            this.confirmDialogVisible = false;
        }
    }

    get interventi(): FormArray {
        return this.form.get('interventi') as FormArray;
    }
}
